import BaseMixin from './base.mixin'
import LocalStorageMixin from '@/shared/mixins/local-storage.mixin'
import AuthMixin from '@/shared/mixins/auth.mixin'

const mixin = {
  mixins: [BaseMixin, LocalStorageMixin, AuthMixin],
  methods: {
    async getReviewExamsList({ page, pageSize = 10, patientName = '', examId = '' }) {
      try {
        const params = []
        if (page) params.push(`Page=${page}`)
        if (pageSize) params.push(`PageSize=${pageSize}`)
        if (patientName) params.push(`PatientName=${encodeURIComponent(patientName)}`)
        if (examId) params.push(`ExamId=${examId}`)
        const query = params.length ? '?' + params.join('&') : ''
        const url = `/core/FastReport/Review${query}`

        const response = await this.requestGet(url)
        if (response && response.data) {
          return {
            data: response.data.items,
            totalCount: response.data.totalCount
          }
        } else {
          throw new Error('Resposta da API inválida')
        }
      } catch (error) {
        console.error('Erro ao buscar lista de exames:', error)
        throw new Error('Erro ao buscar lista de exames: ' + error.message)
      }
    },
    async getDetails(examId) {
      try {
        const response = await this.requestGet(`/core/Exams/${examId}/`)
        if (response && response.data) {
          return response.data
        } else {
          throw new Error('Resposta da API inválida')
        }
      } catch (error) {
        console.error('Erro ao buscar detalhes do exame:', error)
        return ''
      }
    },
    async examRepeatRadio(examId, reason) {
      try {
        const response = await this.requestPost(`/core/FastReport/${examId}/Repeat`, {
          reason
        })

        if (response && response.data) {
          return response.data
        } else {
          throw new Error('Resposta da API inválida')
        }
      } catch (error) {
        throw new Error('Erro ao repetir o exame: ' + error.message)
      }
    },

    async getExamNotes(examId) {
      try {
        const response = await this.requestGet(`/core/FastReport/${examId}/Notes`)
        if (response && response.data) {
          return response.data.notes
        } else {
          throw new Error('Resposta da API inválida')
        }
      } catch (error) {
        throw new Error('Erro ao buscar relatório do exame: ' + error.message)
      }
    },

    async getMaskOptions(specialityId) {
      try {
        const endpoint = `/core/Physician/Mask?specialityId=${specialityId}`
        const response = await this.requestGet(endpoint)
        if (!response || !response.data) {
          console.warn('No data in mask response')
          return { data: [] }
        }

        return response
      } catch (error) {
        console.error('Error in getMaskOptions:', error)
        if (error.response) {
          console.error('Error response:', error.response)
        }
        return { data: [] }
      }
    },

    async sendReportRadio(examId, payload) {
      try {
        const response = await this.requestPost(`/core/FastReport/${examId}/Report`, payload, {
          headers: { 'Content-Type': 'application/json' }
        })
        return response
      } catch (error) {
        throw new Error(
          'Erro ao enviar o relatório: ' + (error.response?.data?.errors[0]?.message || 'Erro desconhecido')
        )
      }
    },

    async duplicateExam(examId) {
      try {
        const response = await this.requestPost('/core/Exam/Clone', {examId})
        if (response && response.data) {
          return response.data.data
        }
      } catch (error) {
        throw new Error('Erro ao duplicar exame: ' + error.message)
      }
    },

    async sendMaintainReport(payload) {
      try {
        const response = await this.requestPost(`/core/FastReport/Review`, payload, {
          headers: { 'Content-Type': 'application/json' }
        })
        return response
      } catch (error) {
        const msg =
          error?.response?.data?.errors?.[0]?.message ||
          error?.response?.data?.message ||
          error.message ||
          'Erro desconhecido'
        console.error('[sendMaintainReport] Erro:', msg)
        throw new Error('Erro ao enviar o relatório: ' + msg)
      }
    },
    async getReasonList() {
      try {
        const response = await this.requestGet('/core/Administrator/Exams/RetificationReasons')
        if (response && response.data) {
          return response.data
        } else {
          throw new Error('Resposta da API inválida')
        }
      } catch (error) {
        throw new Error('Erro ao buscar lista de motivos: ' + error.message)
      }
    },
    async sendReviewReport(payload) {
      try {
        const response = await this.requestPost(`/core/FastReport/Review`, payload, {
          headers: { 'Content-Type': 'application/json' }
        })
        return response
      } catch (error) {
        throw new Error(
          'Error ao enviar o relatório: ' + (error.response?.data?.errors[0]?.message || 'Erro desconhecido')
        )
      }
    },
    async getReportDetails(examId) {
      try {
        const response = await this.requestGet(`/core/FastReport/${examId}/ReviewDetails`)
        if (response) {
          return response.data.description
        } else {
          throw new Error('Resposta da API inválida')
        }
      } catch (error) {
        throw new Error('Erro ao buscar relatório do exame: ' + error.message)
      }
    },
    async getPhysicianDisplayOptions() {
      const res = await this.requestGet('/core/Physician/DisplayOptions')
      return res.data
    },

    async sendReportFromReviewer(examId, payload) {
      try {
        const response = await this.requestPost(`/core/FastReport/${examId}/ReportFromReviewer`, payload, {
          headers: { 'Content-Type': 'application/json' }
        })
        return response
      } catch (error) {
        const msg =
          error?.response?.data?.errors?.[0]?.message ||
          error?.response?.data?.message ||
          error.message ||
          'Erro desconhecido'
        throw new Error('Erro ao enviar o relatório: ' + msg)
      }
    },

    async getPreviousExamsByExamId(examId) {
      try {
        const response = await this.requestGet(`/core/Physician/Exams/${examId}/Previous`)
        if (response && response.data) {
          return response.data
        } else {
          throw new Error('Resposta da API inválida')
        }
      } catch (error) {
        console.error('Erro ao buscar exames anteriores:', error)
        return []
      }
    },

    async getFreeFilteredExams(filters, examId) {
      try {
        if (!examId) {
          return []
        }
        const query = new URLSearchParams()
        if (examId) query.append('ExamId', examId)
        if (filters.name) query.append('Name', filters.name)
        // Adapta a data de nascimento pro formato americano
        if (filters.dateOfBirth) {
          const [year, month, day] = filters.dateOfBirth.split('-')
          const usDate = `${month}-${day}-${year}`
          query.append('DateOfBirth', usDate)
        }
        if (filters.document) query.append('Document', filters.document)
        if (filters.gender) query.append('Gender', filters.gender)

        const finalUrl = `/core/Exams?${query.toString()}`
        const response = await this.requestGet(finalUrl)
        return response?.data || []
      } catch (error) {
        console.error('Erro ao buscar exames com filtro livre:', error)
        return []
      }
    }
  }
}

export default mixin
